import {
  INTAKE_FORM_ERROR_CODES,
  type IntakeFormRefusal,
} from '@/definitions/intakeForm'
import { isTimeoutError } from '@/utils/apiErrors'

import { describeSaveFailure } from './describeSaveFailure'
import { isFormGone } from './isFormGone'
import { parseFormTemplate } from './parseFormTemplate'
import { readRefusal } from './readRefusal'

interface SaveAttempt {
  displayName: string
  formTemplate: unknown
}

interface RunIntakeFormSaveArgs<Saved> {
  displayName: string
  formTemplateJson: string
  goneMessage?: string
  onNameRefused: (message: string) => void
  onSaved: (saved: Saved) => void
  save: (attempt: SaveAttempt) => Promise<Saved>
  setProblem: (problem: string | null) => void
  setRefusal: (refusal: IntakeFormRefusal | null) => void
  timeoutMessage: string
}

// An empty warning list is an acknowledgement of nothing, on a write nothing here
// can undo, so that refusal is not offered as a second press.
export const awaitingWarningConfirmation = (
  refusal: IntakeFormRefusal | null
): boolean => {
  const warnings = refusal?.warnings ?? []
  return (
    refusal?.code === INTAKE_FORM_ERROR_CODES.UNACKNOWLEDGED_WARNINGS &&
    warnings.length > 0
  )
}

export const runIntakeFormSave = async <Saved,>({
  displayName,
  formTemplateJson,
  goneMessage,
  onNameRefused,
  onSaved,
  save,
  setProblem,
  setRefusal,
  timeoutMessage,
}: RunIntakeFormSaveArgs<Saved>): Promise<void> => {
  const parsed = parseFormTemplate(formTemplateJson)
  if (!parsed.ok) {
    setRefusal(null)
    setProblem(parsed.message)
    return
  }

  // Stored verbatim, so a trailing space would be permanent.
  const trimmed = displayName.trim()

  try {
    const saved = await save({
      displayName: trimmed,
      formTemplate: parsed.value,
    })
    onSaved(saved)
  } catch (error) {
    if (goneMessage !== undefined && isFormGone(error)) {
      setRefusal(null)
      setProblem(goneMessage)
      return
    }
    if (isTimeoutError(error)) {
      setRefusal(null)
      setProblem(timeoutMessage)
      return
    }
    const named = readRefusal(error)
    if (named?.code === INTAKE_FORM_ERROR_CODES.DUPLICATE_FORM_NAME) {
      onNameRefused(named.message)
      return
    }
    setRefusal(named)
    setProblem(named === null ? describeSaveFailure(error) : null)
  }
}
